import { useState } from "react";
import type { Activity, CardView } from "@breeze/shared";
import { useActivity, usePostNote } from "../hooks/useActivity.js";

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

export function NotesTab({ card }: { card: CardView }): JSX.Element {
  const q = useActivity(card.cardId);
  const post = usePostNote(card.cardId);
  const [draft, setDraft] = useState("");

  const notes: Activity[] = (q.data?.activity ?? []).filter(
    (a) => a.type === "note"
  );

  const handleSubmit = (): void => {
    const trimmed = draft.trim();
    if (!trimmed || post.isPending) return;
    post.mutate(
      { note: trimmed },
      {
        onSuccess: () => setDraft(""),
      }
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        {q.isLoading ? (
          <div className="rounded-md border border-ink-700 bg-ink-900/40 px-4 py-6 text-center text-[13px] text-ink-200">
            loading notes…
          </div>
        ) : notes.length === 0 ? (
          <section className="rounded-md border border-dashed border-ink-500 bg-ink-900/40 px-4 py-8 text-center">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink-50">
              Notes
            </div>
            <p className="mx-auto mt-2 max-w-xs text-[13px] text-ink-200">
              Internal notes for the team. Customers never see these.
            </p>
          </section>
        ) : (
          <ul className="flex flex-col gap-2">
            {notes.map((n) => (
              <li
                key={n.activityId}
                className="rounded-md border border-ink-700 bg-ink-900/50 px-3 py-2.5"
              >
                <div className="flex items-center gap-2 text-[12px] text-ink-200">
                  <span className="font-semibold text-ink-50">{n.actor ?? "unknown"}</span>
                  <span className="ml-auto tabular-nums">{formatWhen(n.createdAt)}</span>
                </div>
                <div className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-snug text-ink-50">
                  {n.noteBody}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3 border-t border-ink-700 pt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note (internal only)…"
          rows={3}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleSubmit();
          }}
          className="w-full resize-none rounded-md border border-ink-500 bg-ink-900 px-2.5 py-2 text-[13px] leading-snug text-ink-50 outline-none placeholder:text-ink-200 focus:border-slack-aubergineLight"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[12px] text-ink-200">
            {post.isError ? (
              <span className="text-slack-pink">save failed</span>
            ) : post.isPending ? (
              <span className="text-slack-yellow">saving…</span>
            ) : (
              <>⌘↵ to save · {notes.length} note{notes.length === 1 ? "" : "s"}</>
            )}
          </span>
          <button
            onClick={handleSubmit}
            disabled={!draft.trim() || post.isPending}
            className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 transition-colors hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:border-ink-600 disabled:bg-ink-700 disabled:text-ink-300"
          >
            Add note
          </button>
        </div>
      </div>
    </div>
  );
}
